import * as React from 'react';

import type { W_ReportType_FindAllReportTypesForFilter_VM } from 'ab-api/web/reportType/findAllForFilter';
import type { W_ReportType_FindReportTypeViewTable_VM, W_ReportType_FindReportTypeViewTable_VM_Row } from 'ab-api/web/workOrderReport/reportTypeViewTable';

import { getId } from '@acceligentllc/shared/utils/array';

import type { TimePeriodRecurrence } from '@acceligentllc/shared/enums/timePeriodRecurrence';
import { WorkOrderStatus } from '@acceligentllc/shared/enums/workOrderStatus';

import { useLazyLoad } from 'af-utils/react.util';

import { Button } from '@acceligentllc/storybook';

import Checkbox from 'af-components/Controls/Checkbox';
import Dropdown from 'af-components/Controls/Dropdown';
import CustomModal from 'af-components/CustomModal';
import LoadingIndicator from 'af-components/LoadingIndicator';
import RectangleButton from 'af-components/MultipleOptionsButton/RectangleButton';

import ReportTypeViewTableDateFilter from '../DateFilter';
import ReportTypeRow from './ReportTypeRow';
import styles from './styles.module.scss';
import { ReportTypeFilterItem } from '../ReportTypeFilter/types';
import type { ReportTypeViewTableAdditionalFilter } from '../types';

const _filterNonCanceledFieldReportTypes =
	(_fieldReportType: W_ReportType_FindReportTypeViewTable_VM_Row) => _fieldReportType.workOrderStatus !== WorkOrderStatus.CANCELED;

interface OwnProps {
	closeModal: () => void;
	showModal: boolean;
	selectedReportType: Nullable<ReportTypeFilterItem>;
	loadReportTypes: () => Promise<W_ReportType_FindAllReportTypesForFilter_VM>;
	selectedStartDate: Date;
	selectedEndDate: Date;
	selectedPeriod: TimePeriodRecurrence;
	fetchData: (startDate: Date, endDate: Date, page: number, limit: number, filterText: string, sortBy: '' | 'reportTypeName' | 'workOrderCode' | 'companyName' | 'startDate' | 'calculatedReportDisplayStatus' | 'reportTypeStatus', sortAscending: boolean, filterByReportTypeId: number, additionalFilters: ReportTypeViewTableAdditionalFilter[]) => Promise<W_ReportType_FindReportTypeViewTable_VM>;
	onExportAsPDFClick: (fieldReportTypeIds: number[], isForCustomer: boolean, reportName: string) => () => Promise<void>;
	fiscalYearStartMonth: Nullable<number>;
}

type Props = OwnProps;

const ExportReportTypesModal: React.FC<Props> = (props) => {
	const {
		closeModal,
		showModal,
		selectedReportType,
		loadReportTypes,
		selectedEndDate,
		selectedPeriod,
		selectedStartDate,
		fetchData,
		onExportAsPDFClick,
		fiscalYearStartMonth,
	} = props;

	const [filterByReportType, setFilterByReportType] = React.useState<Nullable<ReportTypeFilterItem>>(selectedReportType ?? null);
	const [startDate, setStartDate] = React.useState(selectedStartDate);
	const [endDate, setEndDate] = React.useState(selectedEndDate);
	const [period, setPeriod] = React.useState(selectedPeriod);
	const [reportTypes, setReportTypes] = React.useState<Nullable<W_ReportType_FindReportTypeViewTable_VM>>(null);
	const [selectedReportTypes, setSelectedReportTypes] = React.useState<Set<number>>(new Set([]));
	const [showFieldsHiddenFromCustomer, setShowFieldsHiddenFromCustomer] = React.useState(true);
	const [isLoading, setIsLoading] = React.useState(false);

	const loadAndSetReportTypes = React.useCallback(async () => {
		const fetchedReportTypes = await loadReportTypes();
		return ReportTypeFilterItem.bulkConstructor(fetchedReportTypes);
	}, [loadReportTypes]);

	const { options: reportTypeOptions, lazyLoad: lazyLoadReportTypes } = useLazyLoad(loadAndSetReportTypes);

	const filterTableByReportType = React.useCallback((value) => {
		const selected = reportTypeOptions.find((_value) => _value.id === value.id);
		setFilterByReportType(selected ?? null);
	}, [reportTypeOptions]);

	const renderReportTypeMenuSelectedItem = React.useCallback((_reportType: ReportTypeFilterItem) => {
		return <>{_reportType?.name ? `${_reportType.name}` : ''}</>;
	}, []);

	const renderReportTypeMenuItem = React.useCallback((optionItem: ReportTypeFilterItem) => {
		return !!optionItem
			? <>{optionItem.name}</>
			: <>All report types</>;
	}, []);

	const fetchAndSetData = React.useCallback(async () => {
		if (showModal && startDate && endDate && period && filterByReportType) {
			setIsLoading(true);
			const fetchedData = await fetchData(
				startDate,
				endDate,
				0,
				1000,
				'',
				'reportTypeName',
				false,
				filterByReportType.id,
				[]
			);
			const nonCanceledFieldReportTypes = fetchedData.rows.filter(_filterNonCanceledFieldReportTypes);
			setReportTypes({
				pages: 1,
				totalCount: nonCanceledFieldReportTypes.length,
				rows: nonCanceledFieldReportTypes,
			});
			setIsLoading(false);
			setSelectedReportTypes(new Set(nonCanceledFieldReportTypes.map(getId)));
		}

	}, [endDate, fetchData, filterByReportType, period, showModal, startDate]);

	React.useEffect(() => {
		fetchAndSetData();
	}, [fetchAndSetData]);

	const onCheckboxClick = React.useCallback((isChecked: boolean, fieldReportTypeId: number) => {
		const newSelectedReportTypes: Set<number> = new Set(selectedReportTypes);
		if (isChecked) {
			newSelectedReportTypes.delete(fieldReportTypeId);
		} else {
			newSelectedReportTypes.add(fieldReportTypeId);
		}
		setSelectedReportTypes(newSelectedReportTypes);
	}, [selectedReportTypes]);

	const numberOfItemsSelectedText = React.useMemo(() => {
		if (!reportTypes) {
			return null;
		}
		return !!selectedReportTypes.size ? `${selectedReportTypes.size} item(s) selected` : 'No items selected';
	}, [reportTypes, selectedReportTypes.size]);

	const onSelectAllClick = React.useCallback(() => {
		if (!reportTypes) {
			return;
		}
		const selectedAllSet = new Set(Array.from(reportTypes.rows.map(getId)));
		setSelectedReportTypes(selectedAllSet);
	}, [reportTypes]);

	const onClearClick = React.useCallback(() => {
		const selectedAllSet = new Set([]);
		setSelectedReportTypes(selectedAllSet);
	}, []);

	const reportTypesMapper = React.useCallback((_reportType: W_ReportType_FindReportTypeViewTable_VM_Row) => {
		return (
			<ReportTypeRow
				isChecked={selectedReportTypes.has(_reportType.id)}
				key={_reportType.id}
				onCheckboxClick={onCheckboxClick}
				reportType={_reportType}
			/>
		);
	}, [onCheckboxClick, selectedReportTypes]);

	const onShowFieldsHiddenFromCustomerClick = React.useCallback(
		() => setShowFieldsHiddenFromCustomer(!showFieldsHiddenFromCustomer)
		, [showFieldsHiddenFromCustomer]);

	const renderContent = React.useCallback(() => {

		if (isLoading) {
			return (
				<div className={styles['report-type-list']}>
					<div className={styles['report-type-list--empty']}>
						<LoadingIndicator color="orange" />
					</div>
				</div>
			);
		}
		if (reportTypes?.rows.length === 0) {
			return (
				<div className={styles['report-type-list']}>
					<div className={styles['report-type-list--empty']}>
						No rows found.
					</div>
				</div>
			);
		}
		return (
			<div className={styles['report-type-list']}>
				{reportTypes
					? reportTypes.rows.map(reportTypesMapper)
					: (
						<div className={styles['report-type-list--non-selected']}>
							Select Report type to display data
						</div>
					)
				}
			</div>
		);
	}, [isLoading, reportTypes, reportTypesMapper]);

	const onExportToPDFClick = React.useCallback(async () => {
		onExportAsPDFClick(Array.from(selectedReportTypes), !showFieldsHiddenFromCustomer, filterByReportType?.name ?? '')();
		closeModal();
	}, [closeModal, filterByReportType?.name, onExportAsPDFClick, selectedReportTypes, showFieldsHiddenFromCustomer]);

	const hasSelectableFieldReportTypes = reportTypes?.rows.length;

	return (
		<CustomModal
			className="report-type-modal"
			closeModal={closeModal}
			modalStyle="info"
			showModal={showModal}
			size="md"
		>
			<CustomModal.Header
				closeModal={closeModal}
				title="Export Report Types"
			/>
			<CustomModal.Body>
				<div className={styles.container}>
					Select Report Types to export to PDF:
					<div className={styles['job-report-type']}>
						<span className={styles['filter-container']}>
							<Dropdown<ReportTypeFilterItem>
								className={styles.filter}
								defaultValue={selectedReportType ?? null}
								filterable={true}
								filterBy={['name']}
								id="report-type-view-rt-filter-modal"
								labelKey="name"
								onLazyLoad={lazyLoadReportTypes}
								onValueChange={filterTableByReportType}
								options={reportTypeOptions}
								placeholder="Select Report Type"
								renderMenuItem={renderReportTypeMenuItem}
								renderSelected={renderReportTypeMenuSelectedItem}
								valueKey="id"
								withBorder={false}
							/>
							<RectangleButton
								action={undefined}
								isLeftOpen={true}
								isSquare={true}
								label={<span className="icon-filter" />}
							/>
						</span>
					</div>
					<div className={styles['customer-fields']}>
						<Checkbox handleChange={onShowFieldsHiddenFromCustomerClick} isChecked={showFieldsHiddenFromCustomer} />
						Show fields hidden from customer
					</div>
					<div className={styles['customer-fields-hint']}>
						Report types with no fields visible to customer will not be included in the export
					</div>
					<div className={styles['date-filter']}>
						<ReportTypeViewTableDateFilter
							endDate={endDate}
							fiscalYearStartMonth={fiscalYearStartMonth}
							period={period}
							setEndDate={setEndDate}
							setPeriod={setPeriod}
							setStartDate={setStartDate}
							startDate={startDate}
						/>
					</div>
					{(!!numberOfItemsSelectedText && !!hasSelectableFieldReportTypes) && (
						<div className={styles['number-of-rows-selected']}>
							{numberOfItemsSelectedText}
						</div>
					)}
					{!!hasSelectableFieldReportTypes && (
						<div className={styles['group-actions']}>
							<span onClick={onSelectAllClick}>
								Select All
							</span>
							<span onClick={onClearClick}>
								Clear
							</span>
						</div>
					)}
					<div className={styles['report-type-list']}>
						{renderContent()}
					</div>
				</div>
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					label="Cancel"
					onClick={closeModal}
					style="secondary"
				/>
				<Button
					disabled={!selectedReportTypes.size}
					label="Export To PDF"
					onClick={onExportToPDFClick}
					style="primary"
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

export default React.memo(ExportReportTypesModal);
